import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

export const Card = styled.div`
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  padding: 40px;
  width: 100%;
  max-width: 660px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeUp} 0.5s ease;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Brand = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #a78bfa 0%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const Tagline = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 400;
  letter-spacing: 0.2px;
`;

export const UploadZone = styled.div`
  border: 2px dashed ${({ $dragging }) => $dragging ? 'rgba(167, 139, 250, 0.7)' : 'rgba(255, 255, 255, 0.12)'};
  border-radius: 18px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  background: ${({ $dragging }) => $dragging ? 'rgba(167, 139, 250, 0.08)' : 'rgba(255, 255, 255, 0.02)'};

  &:hover {
    border-color: rgba(167, 139, 250, 0.45);
    background: rgba(167, 139, 250, 0.04);
  }
`;

export const UploadIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(96, 165, 250, 0.15));
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const UploadText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  text-align: center;
  line-height: 1.5;

  strong {
    color: #a78bfa;
    font-weight: 600;
  }
`;

export const UploadHint = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.25);
  font-size: 0.72rem;
`;

export const PreviewContainer = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  animation: ${fadeUp} 0.4s ease;
`;

export const PreviewImage = styled.img`
  width: 100%;
  max-height: 380px;
  object-fit: cover;
  display: block;
  border-radius: 18px;
`;

export const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
`;

export const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeUp} 0.4s ease;
`;

export const SectionLabel = styled.span`
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 8px;
`;

export const Caption = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  line-height: 1.65;
  font-weight: 400;
`;

export const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  background: rgba(167, 139, 250, 0.12);
  color: #c4b5fd;
  border: 1px solid rgba(167, 139, 250, 0.25);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(167, 139, 250, 0.2);
  }
`;

export const UploadButton = styled.button`
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: white;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  letter-spacing: 0.3px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(124, 58, 237, 0.45);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

export const LoaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.82rem;
  animation: ${pulse} 1.5s ease infinite;
`;

export const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top-color: #a78bfa;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
  flex-shrink: 0;
`;
